import { useTranslation } from '@/hooks/useTranslation';
import { PublicationDestination } from '@/pages/browse/hooks/usePublicationDestinations';
import { BrowseActiveFilter, FILTER_TYPE } from '@/pages/browse/types';

import { matchesAnyText } from './filterQuery';
import { FilterMenuItem } from './FilterMenuItem';
import { FilterSubmenu } from './FilterSubmenu';

export const PUBLICATION_STATUS = [
    { key: 'all', label: 'ALL' },
    { key: 'APPROVED', label: 'REVIEW.APPROVED' },
    { key: 'RECEIVED', label: 'REVIEW.RECEIVED' },
    { key: 'REJECTED', label: 'REVIEW.REJECTED' },
    { key: 'REVIEWED', label: 'REVIEW.REVIEWED' },
    { key: 'MISSING', label: 'REVIEW.MISSING' },
] as const;

interface FilterListPublicationsProps {
    destinations: PublicationDestination[];
    query: string;
    onSelect: (type: string, filter: BrowseActiveFilter) => void;
}

export function FilterListPublications({
    destinations,
    query,
    onSelect,
}: FilterListPublicationsProps) {
    const { t } = useTranslation();

    return (
        <>
            {destinations.map((destination) => {
                const publishedToLabel = t('PUBLISHED_TO', { destination: destination.name });
                const matchingStatuses = PUBLICATION_STATUS.filter((status) =>
                    matchesAnyText(query, t(status.label), status.key),
                );
                const destinationMatches = matchesAnyText(
                    query,
                    destination.name,
                    publishedToLabel,
                    String(destination.key),
                );
                const hasMatchingChildren = matchingStatuses.length > 0;

                if (!destinationMatches && !hasMatchingChildren) {
                    return null;
                }

                const statuses = destinationMatches ? PUBLICATION_STATUS : matchingStatuses;

                return (
                    <FilterSubmenu
                        key={String(destination.key)}
                        label={publishedToLabel}
                        query={query}
                        matchesQuery={destinationMatches}
                        hasMatchingChildren={hasMatchingChildren}
                    >
                        {statuses.map((status) => (
                            <li key={status.key} className="tw-list-none">
                                <FilterMenuItem
                                    className="tw-pl-8"
                                    onSelect={() => {
                                        const isAll = status.key === 'all';
                                        const statusLabel = t(status.label);
                                        onSelect(FILTER_TYPE.PUBLISHED_TO, {
                                            name: isAll
                                                ? destination.name
                                                : `${destination.name} : ${statusLabel}`,
                                            type: FILTER_TYPE.PUBLISHED_TO,
                                            itemsQuery: isAll
                                                ? { publicationDestination: String(destination.key) }
                                                : {
                                                    reviewer: String(destination.key),
                                                    reviewStatus: status.key,
                                                },
                                        });
                                    }}
                                >
                                    {t(status.label)}
                                </FilterMenuItem>
                            </li>
                        ))}
                    </FilterSubmenu>
                );
            })}
        </>
    );
}
